import Cookies from 'universal-cookie';
import { SearchRequest, useConfig } from '../../api/api';
import { Box, Button, Chip, IconButton, useTheme, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchField } from './search/SearchField';
import ExoDialog from './ExoDialog';
import { SearchResponseGroup } from './search/SearchResponseGroup';
import { SearchCategoryFilter } from './search/SearchCategoryFilter';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArchiveIcon from '@mui/icons-material/Archive';
import { tokens } from '../../global/theme/tokens';
import { TbSpace } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { searchResultMap, PRIORITY } from './search/searchResultsMap';
import { useUserPreferences } from '../../global/useUserPreferences';

export const ExoSmartSearch = () => {
  const config = useConfig();
  const cookies = new Cookies();
  return (
    <Box>
      {config && (
        <SearchClient
          url={config.searchUrl}
          indexes={cookies.get('searchIndexes')}
        />
      )}
    </Box>
  );
};

const SearchClient = ({ url, indexes }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const cookies = new Cookies();
  const searchPrefix = cookies.get('searchPrefix');
  const location = useLocation();
  const { preferences, updatePreferences } = useUserPreferences();

  const autoFilterMap = {
    /*'/emails': {
      label: t('only emails'),
      Icon: searchResultMap.imap_emails.Icon,
      indexes: [`${searchPrefix}imap_emails`],
    },*/
    '/invoices': {
      label: t('only invoices'),
      Icon: searchResultMap.invoices.Icon,
      indexes: [`${searchPrefix}invoices`],
    },
    '/offers': {
      label: t('only offers'),
      Icon: searchResultMap.offers.Icon,
      indexes: [`${searchPrefix}offers`],
    },
    '/orders': {
      label: t('only orders'),
      Icon: searchResultMap.orders.Icon,
      indexes: [`${searchPrefix}orders`],
    },
    '/leads': {
      label: t('only leads'),
      Icon: searchResultMap.person_leads.Icon,
      indexes: [`${searchPrefix}company_leads`, `${searchPrefix}person_leads`],
    },
    '/expenses': {
      label: t('only expenses'),
      Icon: searchResultMap.person_leads.Icon,
      indexes: [`${searchPrefix}receipts`],
    },
    '/companies': {
      label: t('only companies'),
      Icon: searchResultMap.companies.Icon,
      indexes: [`${searchPrefix}companies`],
    },
    '/contacts': {
      label: t('only contacts'),
      Icon: searchResultMap.contacts.Icon,
      indexes: [`${searchPrefix}contacts`],
    },
    '/projects': {
      label: t('only projects'),
      Icon: searchResultMap.projects.Icon,
      indexes: [`${searchPrefix}projects`],
    },
  };

  const [open, setOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const searchTextRef = useRef('');

  // Lade gespeicherte Kategorie-Filter aus Preferences
  useEffect(() => {
    if (preferences?.searchCategories) {
      setSelectedCategories(preferences.searchCategories);
    }
  }, [preferences]);

  // Speichere Kategorie-Filter bei Änderung
  const handleCategoryChange = (newCategories) => {
    setSelectedCategories(newCategories);
    updatePreferences('searchCategories', newCategories);
  };

  // Sortiere Suchergebnisse nach Priorität
  const sortedResults = useMemo(() => {
    if (!searchResults) return null;
    return [...searchResults].sort((a, b) => {
      const typeA = a.indexUid.substring(searchPrefix?.length || 0);
      const typeB = b.indexUid.substring(searchPrefix?.length || 0);
      const priorityA = searchResultMap[typeA]?.priority || PRIORITY.LOW;
      const priorityB = searchResultMap[typeB]?.priority || PRIORITY.LOW;
      return priorityA - priorityB;
    });
  }, [searchResults, searchPrefix]);

  function search(text) {
    searchTextRef.current = text;
    if (!text) return setSearchResults(null);

    var queries = [];
    var indexesToQuery = currentFilter?.indexes || indexes || [];

    // Filtere nach ausgewählten Kategorien (wenn welche ausgewählt sind)
    if (selectedCategories.length > 0 && !currentFilter) {
      indexesToQuery = indexesToQuery.filter((idx) =>
        selectedCategories.some((cat) => idx.includes(cat))
      );
    }

    indexesToQuery.forEach((index) => {
      const query = {
        indexUid: index,
        q: text,
        limit: 10,
      };
      if (currentFilter?.filter) {
        query.filter = currentFilter.filter;
      }
      // Projekte standardmäßig nur aktive (status=0), archivierte nur wenn explizit gewählt
      if (index.includes('projects') && !currentFilter?.filter) {
        query.filter = includeArchived
          ? 'status = 0 OR status = 1 OR status = 2'
          : 'status = 0';
      }
      queries.push(query);
    });

    const searchData = { queries };

    SearchRequest({
      baseUrl: url,
      path: 'multi-search',
      method: 'post',
      data: searchData,
      onResponse: (res) => setSearchResults(res.results),
    });
  }

  // Bei Änderung eines Filters erneut suchen
  useEffect(() => {
    if (searchTextRef.current) {
      search(searchTextRef.current);
    }
  }, [includeArchived, currentFilter, selectedCategories]);

  useEffect(() => {
    setCurrentFilter(autoFilterMap[location.pathname] || null);
    setIncludeArchived(false);

    // Event listener function for keydown event
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.code === 'Space') {
        event.preventDefault(); // Prevent the default behavior of the key combination
        setOpen(!open);
      }
    };
    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Clean up the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const Icon = currentFilter?.Icon;
  const activeFilterCount =
    (currentFilter ? 1 : selectedCategories.length) + (includeArchived ? 1 : 0);

  return (
    <Box>
      <Button
        onClick={() => setOpen(true)}
        sx={{
          bgcolor: colors.bgInput,
          ':hover': {
            bgcolor: colors.card,
          },
        }}
      >
        <SearchIcon />
        <Typography className="px-1 hidden md:block">{t('Search')}</Typography>
        <Box className=" justify-center items-center gap-1 pl-4 hidden md:flex opacity-50">
          {t('CTRL')} +
          <TbSpace size={15} />
        </Box>
      </Button>
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <Box
          className="max-h-full w-11/12 flex flex-col mx-auto pt-6 gap-4"
          sx={{ maxWidth: '800px' }}
        >
          {/* Suchfeld + Filter-Button */}
          <Box className="flex gap-2 w-full items-center">
            <Box className="flex-1">
              <SearchField onChange={search} />
            </Box>
            <IconButton
              onClick={() => setShowCategoryFilter(!showCategoryFilter)}
              sx={{
                bgcolor: showCategoryFilter ? colors.info + '30' : colors.bgInput,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: showCategoryFilter ? colors.info + '80' : 'transparent',
                '&:hover': { bgcolor: colors.info + '20' },
              }}
            >
              <FilterListIcon
                sx={{
                  color: activeFilterCount > 0 ? colors.info : colors.text,
                }}
              />
            </IconButton>
          </Box>

          {/* Aktive Filter als Chips anzeigen */}
          {activeFilterCount > 0 && (
            <Box className="flex flex-wrap gap-2">
              {currentFilter && (
                <Chip
                  icon={<Icon sx={{ fontSize: '16px', color: colors.info }} />}
                  label={currentFilter.label}
                  onDelete={() => setCurrentFilter(null)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
                  size="small"
                  variant="outlined"
                  sx={{
                    px: '8px',
                    bgcolor: colors.info + '30',
                    borderColor: colors.info + '80',
                    '& .MuiChip-deleteIcon': {
                      color: colors.grey[500],
                      '&:hover': { color: colors.error },
                    },
                  }}
                />
              )}
              {!currentFilter &&
                selectedCategories.map((cat) => {
                  const config = searchResultMap[cat];
                  if (!config) return null;
                  const CatIcon = config.Icon;
                  return (
                    <Chip
                      key={cat}
                      icon={<CatIcon sx={{ fontSize: '14px', color: colors.info }} />}
                      label={t(config.label)}
                      onDelete={() =>
                        handleCategoryChange(selectedCategories.filter((c) => c !== cat))
                      }
                      deleteIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
                      size="small"
                      variant="outlined"
                      sx={{
                        px: '8px',
                        bgcolor: colors.info + '30',
                        borderColor: colors.info + '80',
                        '& .MuiChip-deleteIcon': {
                          color: colors.grey[500],
                          '&:hover': { color: colors.error },
                        },
                      }}
                    />
                  );
                })}
              {includeArchived && (
                <Chip
                  icon={<ArchiveIcon sx={{ fontSize: '16px', color: colors.info }} />}
                  label={t('Archived projects')}
                  onDelete={() => setIncludeArchived(false)}
                  deleteIcon={<CloseIcon sx={{ fontSize: '14px' }} />}
                  size="small"
                  variant="outlined"
                  sx={{
                    px: '8px',
                    bgcolor: colors.info + '30',
                    borderColor: colors.info + '80',
                    '& .MuiChip-deleteIcon': {
                      color: colors.grey[500],
                      '&:hover': { color: colors.error },
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* Aufklappbare Filter-Sektion */}
          {showCategoryFilter && (
            <Box
              className="flex flex-col gap-3 p-3 rounded-lg"
              sx={{ bgcolor: colors.bgInput }}
            >
              {/* Kategorie-Filter */}
              <Box className="flex flex-col gap-2">
                <Box className="flex justify-between items-center">
                  <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                    {t('Filter by category')}
                  </Typography>
                  {(selectedCategories.length > 0 || currentFilter) && (
                    <Typography
                      variant="body2"
                      onClick={() => {
                        handleCategoryChange([]);
                        setCurrentFilter(null);
                        setIncludeArchived(false);
                      }}
                      sx={{
                        color: colors.grey[400],
                        cursor: 'pointer',
                        '&:hover': { color: colors.error },
                      }}
                    >
                      {t('Clear filters')}
                    </Typography>
                  )}
                </Box>
                {currentFilter ? (
                  <Typography variant="body2" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>
                    {t('Remove page filter to use category filters')}
                  </Typography>
                ) : (
                  <SearchCategoryFilter
                    selectedCategories={selectedCategories}
                    onCategoryChange={handleCategoryChange}
                    availableIndexes={indexes}
                  />
                )}
              </Box>

              {/* Archivierte Projekte Toggle */}
              <Box className="flex flex-col gap-2">
                <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                  {t('Projects')}
                </Typography>
                <Box className="flex flex-wrap gap-2">
                  <Chip
                    icon={
                      <ArchiveIcon
                        sx={{
                          fontSize: 14,
                          color: includeArchived ? colors.info : colors.grey[500],
                        }}
                      />
                    }
                    label={t('Include archived')}
                    onClick={() => setIncludeArchived(!includeArchived)}
                    size="small"
                    variant="outlined"
                    sx={{
                      px: '8px',
                      bgcolor: includeArchived ? colors.info + '30' : 'transparent',
                      borderColor: includeArchived ? colors.info + '80' : colors.grey[600],
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: colors.info + '20',
                        borderColor: colors.info + '60',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Suchergebnisse (sortiert nach Priorität) */}
          {sortedResults && (
            <Box className="w-full max-h-full overflow-y-auto flex flex-col gap-4">
              {sortedResults.map((group) => (
                <SearchResponseGroup
                  key={group.indexUid}
                  group={group}
                  onClose={() => setOpen(false)}
                />
              ))}
              {sortedResults.every((g) => g.estimatedTotalHits === 0) && (
                <Box className="text-center py-8">
                  <Typography sx={{ color: colors.grey[400] }}>
                    {t('No results found')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </ExoDialog>
    </Box>
  );
};
